import { useContext } from 'react';
import Image from '@next/image';
import classNames from 'classnames';

import { NOSCRIPT_CLASSES } from 'core/noscript.styles';
import Picture from 'types/Picture';
import { ImagePreset } from 'types/ImagePreset';
import { SrcSetConfig } from 'types/SrcSetConfig';
import { Classes } from 'types/Classes';
import Arrow from '@templates/Slider/components/Arrow';
import Slider from '@templates/Slider';
import { SkeletonImageContext } from '@providers/SkeletonImageProvider';

import PhotoSliderThumbnails from './components/PhotoSliderThumbnails';
import classes from './PhotoSlider.module.scss';

type Props = {
  classes?: Classes<'root' | 'slide' | 'dots' | 'arrow'>;
  mainPhotoPreset: ImagePreset;
  mainPhotoSrcSetConfig: SrcSetConfig[];
  photos: Picture[];
  selectedSlideIndex: number;
  selectedSmallSlideIndex: number;
  slidesPerView: number;
  productTypeId: number;
  id?: number;
  wide?: boolean;
  withDots?: boolean;
  withArrows?: boolean;
  withThumbnails?: boolean;
  onSlideChange: (index: number) => void;
  onSlideClick: (index: number) => void;
  onMainImageClick?: () => void;
};

const PhotoSlider = ({
  id,
  classes: customClasses,
  mainPhotoPreset,
  mainPhotoSrcSetConfig,
  productTypeId,
  photos,
  selectedSlideIndex,
  selectedSmallSlideIndex,
  slidesPerView,
  wide,
  withDots,
  withArrows,
  withThumbnails,
  onSlideChange,
  onSlideClick,
  onMainImageClick = () => {},
}: Props): JSX.Element => {
  const isFullPhoto = mainPhotoPreset === 'p_full_960';
  const { getSkeletonImage } = useContext(SkeletonImageContext);
  const mainSkeletonIcon = getSkeletonImage(mainPhotoPreset, productTypeId);
  const skeletonIcon = getSkeletonImage('p_thumb_3', productTypeId);

  return (
    <>
      <div className={classNames(wide ? classes.modalPhoto : classes.mainPhotoWrapper)}>
        <Slider
          key={wide ? `modal-main-slider-${id}` : `main-slider-${id}`}
          classes={customClasses}
          slideClassName={classes.sliderContainer}
          selectedSlideIndex={selectedSlideIndex}
          onSlideChange={onSlideChange}
          withDots={photos.length > 1 && withDots}
          withArrows={withArrows}
          initialLoaded={[true]}
          leftArrow={({ disabled, onClick }) => (
            <div className={classNames(classes.modalArrow, customClasses?.arrow, classes.leftArrow)} onClick={onClick}>
              <Arrow width={14} variant="left" disabled={disabled} rawIcon />
            </div>
          )}
          rightArrow={({ disabled, onClick }) => (
            <div className={classNames(classes.modalArrow, customClasses?.arrow, classes.rightArrow)} onClick={onClick}>
              <Arrow width={14} variant="right" disabled={disabled} rawIcon />
            </div>
          )}
          lazyLoad
        >
          {photos.map((slide, index) => (
            <div
              key={slide.baseName}
              className={classNames(classes.mainPhoto, { [classes.fullPhoto]: isFullPhoto })}
              onClick={onMainImageClick}
            >
              <Image
                layout="fill"
                objectFit="contain"
                classes={{ root: classes.image, picture: NOSCRIPT_CLASSES.JUSTIFY_CONTENT }}
                alt={`${slide.productName}-${slide.description}`}
                resolveConfig={{
                  preset: mainPhotoPreset,
                  description: slide.description,
                  productName: slide.productName,
                  extension: slide.extension,
                  name: slide.baseName,
                  width: slide.width,
                  height: slide.height,
                  srcSetConfig: mainPhotoSrcSetConfig,
                }}
                skeletonBackground="white"
                skeletonIcon={mainSkeletonIcon}
                skeletonIsExternal={!!mainSkeletonIcon}
                skeletonIconWidth={70}
                skeletonIconHeight={70}
                hasPreload={!index}
                hasLazyLoad={!!index}
                hasLoading={!!index}
              />
            </div>
          ))}
        </Slider>
      </div>
      {photos.length > 1 && withThumbnails && (
        <div className={classes.thumbnailsWrapper}>
          <PhotoSliderThumbnails
            id={id}
            wide={wide}
            isFullPhoto={isFullPhoto}
            slidesPerView={slidesPerView}
            selectedSlideIndex={selectedSlideIndex}
            selectedSmallSlideIndex={selectedSmallSlideIndex}
            skeletonIcon={skeletonIcon}
            photos={photos}
            onSlideClick={onSlideClick}
          />
        </div>
      )}
    </>
  );
};

export default PhotoSlider;
